import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Trophy, Award, Star, Medal, TrendingUp, ArrowRight, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion'
import { useAchievements } from '../contexts/AchievementContext'

const AchievementsHome = () => {
  const { t } = useTranslation()
  const { stats: statSelector, achievements } = useAchievements()

  // Counter Animation Component
  const AnimatedCounter = ({ value, duration = 2 }) => {
    const ref = React.useRef(null)
    const isInView = useInView(ref, { once: true, margin: "-100px" })
    const motionValue = useMotionValue(0)
    const springValue = useSpring(motionValue, { 
      damping: 20,
      stiffness: 50,
      duration: duration * 1000 
    })
    const [displayValue, setDisplayValue] = React.useState('0')

    React.useEffect(() => {
      if (isInView) {
        const numericValue = parseInt(value.replace(/\D/g, ''))
        motionValue.set(numericValue)
      }
    }, [isInView, motionValue, value])

    React.useEffect(() => {
      const unsubscribe = springValue.on('change', (latest) => {
        const suffix = value.includes('+') ? '+' : value.includes('%') ? '%' : ''
        setDisplayValue(Math.floor(latest) + suffix)
      })
      return unsubscribe
    }, [springValue, value])

    return <span ref={ref}>{displayValue}</span>
  }

  const highlights = [
    {
      id: 1,
      title: 'University Toppers',
      description: 'Our students consistently rank among top 10 in Saurashtra University examinations',
      icon: Trophy,
      color: 'from-primary-dark to-primary-mid',
      stat: '15+',
      statLabel: 'Top Rankers',
    },
    {
      id: 2,
      title: '100% Pass Rate',
      description: 'Achieved excellent pass percentage across all programs in 2024-25',
      icon: Award,
      color: 'from-primary-dark to-primary-mid',
      stat: '100%',
      statLabel: 'Success Rate',
    },
    {
      id: 3,
      title: 'Sports Champions',
      description: 'Inter-college cricket and kabaddi champions at district level',
      icon: Medal,
      color: 'from-primary-dark to-primary-mid',
      stat: '25+',
      statLabel: 'Sports Medals',
    },
    {
      id: 4,
      title: 'Cultural Excellence',
      description: 'Multiple awards in state-level cultural competitions and youth festivals',
      icon: Star,
      color: 'from-primary-mid to-primary-dark',
      stat: '30+',
      statLabel: 'Cultural Awards',
    },
  ]

  const recentAchievements = useMemo(() => {
    const list = Array.isArray(achievements)
      ? achievements.filter((a) => a.category !== 'stat' && a.isActive !== false)
      : []
    const sorted = [...list].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    const top = sorted.slice(0, 3)
    if (top.length) {
      return top.map((a) => ({
        title: a.title,
        description: a.description,
        date: a.year || '',
        category: a.category,
      }))
    }
    return [
      {
        title: 'National Level Science Olympiad Winners',
        description: '3 students qualified for national finals in Science Olympiad',
        date: 'November 2025',
        category: 'Academic',
      },
      {
        title: 'Best NSS Unit Award',
        description: 'Received Best NSS Unit award for outstanding social service activities',
        date: 'October 2025',
        category: 'Social Service',
      },
      {
        title: 'Inter-University Debate Champions',
        description: 'Won first prize in Gujarat State Inter-University Debate Competition',
        date: 'September 2025',
        category: 'Cultural',
      },
    ]
  }, [achievements])

  const stats = useMemo(() => {
    const apiStats = statSelector()
    if (apiStats.length) {
      return apiStats.map((s) => ({
        value: s.metricValue,
        label: s.metricLabel,
        icon: ({
          Trophy,
          Award,
          Star,
          Medal,
          TrendingUp,
          Users,
        }[s.icon] || Trophy),
      }))
    }
    return [
      { value: '150+', label: 'Total Awards', icon: Trophy },
      { value: '50+', label: 'University Ranks', icon: Award },
      { value: '200+', label: 'Sports Medals', icon: Medal },
      { value: '100+', label: 'Cultural Prizes', icon: Star },
    ]
  }, [statSelector])

  return (
    <section className="py-16 section-light">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-primary-dark to-primary-mid text-white px-6 py-3 rounded-full mb-4">
            <Trophy className="w-6 h-6" />
            <span className="font-semibold">Celebrating Excellence</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Achievements & <span className="text-primary-dark">Awards</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Our students and institution have earned recognition at district, state, and national levels across academics, sports, and cultural activities.
          </p>
        </motion.div>

        {/* Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <stat.icon className="w-10 h-10 mx-auto mb-3 text-primary-dark" />
              <div className="text-3xl font-bold text-primary-dark mb-2">
                <AnimatedCounter value={stat.value} duration={2.5} />
              </div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Achievement Highlights */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {highlights.map((achievement, index) => (
            <motion.div
              key={achievement.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 h-full">
                <div className={`bg-gradient-to-r ${achievement.color} p-6 text-white`}>
                  <achievement.icon className="w-12 h-12 mb-3" />
                  <div className="text-3xl font-bold mb-1">
                    <AnimatedCounter value={achievement.stat} duration={2} />
                  </div>
                  <div className="text-sm opacity-90">{achievement.statLabel}</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-primary-dark transition-colors duration-300">
                    {achievement.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {achievement.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Recent Achievements */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900 flex items-center">
              <TrendingUp className="w-7 h-7 text-primary-dark mr-3" />
              Recent Achievements
            </h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {recentAchievements.map((achievement, index) => (
              <div
                key={index}
                className="border-l-4 border-primary-mid pl-4 hover:bg-primary-light/10 p-4 rounded-r-lg transition-all duration-300"
              >
                <span className="inline-block px-3 py-1 bg-primary-mid/10 text-primary-dark rounded-full text-xs font-semibold mb-2">
                  {achievement.category}
                </span>
                <h4 className="font-bold text-gray-800 mb-2">{achievement.title}</h4>
                <p className="text-sm text-gray-600 mb-2">{achievement.description}</p>
                <p className="text-xs text-gray-500">{achievement.date}</p>
              </div>
            ))}
          </div>
        </motion.div> */}

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link
            to="/achievements"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary-dark to-primary-mid text-white px-8 py-4 rounded-lg font-bold text-lg hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
          >
            <span>View All Achievements</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

export default AchievementsHome
